import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import Layout from '@/components/Layout';
import RouteFallback from '@/components/RouteFallback';
import RequireAuth from '@/components/RequireAuth';

/*
 * Routes mirror the paths the Next.js app served, so every existing link,
 * bookmark, and email deep-link keeps working after the move to Vite.
 */
const Home = lazy(() => import('@/pages/Home'));
const Services = lazy(() => import('@/pages/Services'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const FindAWriter = lazy(() => import('@/pages/FindAWriter'));
const ApplyWriter = lazy(() => import('@/pages/ApplyWriter'));
const ApplyClient = lazy(() => import('@/pages/ApplyClient'));

const GrantsList = lazy(() => import('@/pages/grants/GrantsList'));
const GrantDetail = lazy(() => import('@/pages/grants/GrantDetail'));
const RequestWriter = lazy(() => import('@/pages/grants/RequestWriter'));

const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'));
const MyRequests = lazy(() => import('@/pages/dashboard/MyRequests'));
const MyRequestDetail = lazy(() => import('@/pages/dashboard/MyRequestDetail'));

const SignIn = lazy(() => import('@/pages/account/SignIn'));
const SignUp = lazy(() => import('@/pages/account/SignUp'));
const Logout = lazy(() => import('@/pages/account/Logout'));
const SetupSubscriber = lazy(() => import('@/pages/account/SetupSubscriber'));
const Billing = lazy(() => import('@/pages/account/Billing'));
const ForgotPassword = lazy(() => import('@/pages/account/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/account/ResetPassword'));

const AdminHome = lazy(() => import('@/pages/admin/AdminHome'));
const AdminGrants = lazy(() => import('@/pages/admin/AdminGrants'));
const AdminGrantNew = lazy(() => import('@/pages/admin/AdminGrantNew'));
const AdminGrantEdit = lazy(() => import('@/pages/admin/AdminGrantEdit'));
const AdminApplications = lazy(() => import('@/pages/admin/AdminApplications'));

function page(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function guarded(element: ReactNode) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <RequireAuth>{element}</RequireAuth>
    </Suspense>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={page(<Home />)} />

        <Route path="grants" element={page(<GrantsList />)} />
        <Route path="grants/:id" element={page(<GrantDetail />)} />
        <Route path="grants/:id/request-writer" element={guarded(<RequestWriter />)} />

        <Route path="services" element={page(<Services />)} />
        <Route path="pricing" element={page(<Pricing />)} />
        <Route path="find-a-writer" element={page(<FindAWriter />)} />
        <Route path="apply-writer" element={page(<ApplyWriter />)} />
        <Route path="apply-client" element={page(<ApplyClient />)} />
        <Route path="privacy" element={page(<Privacy />)} />
        <Route path="terms" element={page(<Terms />)} />

        <Route path="dashboard" element={guarded(<Dashboard />)} />
        <Route path="dashboard/my-requests" element={guarded(<MyRequests />)} />
        <Route path="dashboard/my-requests/:id" element={guarded(<MyRequestDetail />)} />

        <Route path="account/signin" element={page(<SignIn />)} />
        <Route path="account/signup" element={page(<SignUp />)} />
        <Route path="account/logout" element={page(<Logout />)} />
        <Route path="account/setup-subscriber" element={page(<SetupSubscriber />)} />
        <Route path="account/billing" element={guarded(<Billing />)} />
        <Route path="account/forgot-password" element={page(<ForgotPassword />)} />
        <Route path="account/reset-password" element={page(<ResetPassword />)} />

        <Route path="admin" element={guarded(<AdminHome />)} />
        <Route path="admin/grants" element={guarded(<AdminGrants />)} />
        <Route path="admin/grants/new" element={guarded(<AdminGrantNew />)} />
        <Route path="admin/grants/:id/edit" element={guarded(<AdminGrantEdit />)} />
        <Route path="admin/applications" element={guarded(<AdminApplications />)} />

        <Route path="*" element={page(<NotFound />)} />
      </Route>
    </Routes>
  );
}
